import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { C2Button } from '../components/c2';
import AuthShell from '../components/auth/AuthShell';
import AuthInput from '../components/auth/Input';
import OAuthStack from '../components/auth/OAuthStack';
import { api, setToken, type ApiError } from '../lib/api';
import { useCurrentUser } from '../lib/useCurrentUser';

type Mode = 'oauth' | 'email' | 'password';

export default function LoginPage() {
  const navigate = useNavigate();
  const auth = useCurrentUser();
  const [mode, setMode] = useState<Mode>('oauth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (auth.status === 'signed-in' && auth.user) {
      window.location.href =
        auth.user.onboarding_complete === false ? '/onboarding' : '/dashboard';
    }
  }, [auth.status, auth.user]);

  if (auth.status !== 'signed-out') return null;

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email) return;
    if (mode === 'email') {
      setMode('password');
      return;
    }
    if (mode === 'password') {
      if (!password) { setError('Password required'); return; }
      setLoading(true);
      try {
        const { token } = await api.login(email, password);
        setToken(token);
        // Route based on onboarding state — first-time signers-in finish
        // the 5-step setup before they see the dashboard.
        try {
          const me = await api.me();
          window.location.href = me.onboarding_complete === false ? '/onboarding' : '/dashboard';
        } catch {
          window.location.href = '/dashboard';
        }
      } catch (err) {
        setError((err as ApiError).message || 'Sign-in failed');
        setLoading(false);
      }
    }
  };

  const handleMagicLink = async () => {
    setError('');
    if (!email) { setError('Enter your email first'); return; }
    setLoading(true);
    try {
      await api.magicLink(email);
      navigate('/magic-sent', { state: { email } });
    } catch (err) {
      setError((err as ApiError).message || 'Could not send magic link');
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h2
        className="font-display font-extrabold text-ink m-0 mb-2 text-center"
        style={{ fontSize: 34, letterSpacing: '-0.02em' }}
      >
        Welcome back
      </h2>
      <p className="text-[17px] text-ink-soft m-0 mb-7 text-center">
        Sign in to manage your go/ links.
      </p>

      {mode === 'oauth' && <OAuthStack onEmailClick={() => setMode('email')} />}

      {(mode === 'email' || mode === 'password') && (
        <>
          <button
            onClick={() => { setMode('oauth'); setPassword(''); setError(''); }}
            className="bg-transparent border-0 cursor-pointer p-0 mb-4 text-[13px] text-ink-soft font-medium"
          >
            ← Other sign-in options
          </button>
          <form onSubmit={handleContinue}>
            <AuthInput
              label="Email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
            />
            {mode === 'password' && (
              <AuthInput
                label="Password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
              />
            )}
            {error && (
              <p className="text-[13px] mb-3" style={{ color: 'var(--color-peach-deep)' }}>{error}</p>
            )}
            <C2Button variant="accent" size="lg" className="w-full mt-2" type="submit" disabled={loading}>
              {loading ? '…' : mode === 'password' ? 'Sign in' : 'Continue'}
            </C2Button>
          </form>
          <button
            onClick={handleMagicLink}
            disabled={loading}
            className="w-full bg-transparent border-0 cursor-pointer text-[13px] text-ink-soft"
            style={{ padding: '12px 0 4px' }}
          >
            Email me a sign-in link instead
          </button>
        </>
      )}

      <div
        className="mt-6 pt-5 border-t border-hairline flex justify-center gap-6 text-sm text-ink-soft text-center"
      >
        <span>
          New here?{' '}
          <Link to="/gatekeeper" className="font-semibold no-underline" style={{ color: 'var(--accent-solid)' }}>
            Create an account
          </Link>
        </span>
        <Link to="/forgot" className="text-ink-soft no-underline">Forgot password?</Link>
      </div>
    </AuthShell>
  );
}
